/**
 * Creator Helper Utilities
 * 
 * This utility file provides centralized functions for handling creator profile pictures
 * and related data extraction with proper fallback chains.
 */

/**
 * Get creator profile image URL with proper fallback chain
 *
 * Priority order:
 * 1. creator.twitter.profileImageUrl (canonical field from backend)
 * 2. unavatar.io fallback via twitter username
 * 3. null (if nothing found)
 *
 * @param {Object} creator - The creator object
 * @returns {string|null} - Profile image URL or null if not found
 */
const stripAtPrefix = (value) => value?.replace(/^@/, '') || '';

const getRawTwitterUsername = (creator) => {
  return creator?.twitter?.username ||
         creator?.twitterUsername ||
         creator?.twitterHandle ||
         creator?.username ||
         creator?.handle ||
         creator?.twitter?.handle ||
         '';
};

export const getCreatorHandle = (creator) => {
  const username = stripAtPrefix(getRawTwitterUsername(creator)).trim();
  return username ? `@${username}` : '';
};

export const isGeneratedCreatorName = (name, username) => {
  if (!name || !username) return false;

  const cleanName = stripAtPrefix(String(name).trim());
  const cleanUsername = stripAtPrefix(String(username).trim());
  if (!cleanName || !cleanUsername) return false;

  const escapedUsername = cleanUsername.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`^${escapedUsername}_\\d+$`, 'i').test(cleanName);
};

export const getCleanCreatorName = (creator) => {
  if (!creator) return '';

  const username = getRawTwitterUsername(creator);
  const handle = getCreatorHandle(creator);
  const rawName = creator.name ||
                  creator.displayName ||
                  creator.twitter?.displayName ||
                  creator.twitter?.name ||
                  '';

  if (isGeneratedCreatorName(rawName, username)) return handle;
  return rawName || handle;
};

export const getCreatorProfileImage = (creator) => {
  if (!creator) return null;

  // Primary: stored profile image from Twitter API (highest quality, _400x400)
  if (creator.twitter?.profileImageUrl) {
    return creator.twitter.profileImageUrl;
  }

  // Secondary: unavatar.io fallback via Twitter handle
  if (creator.twitter?.username) {
    return `https://unavatar.io/twitter/${creator.twitter.username}`;
  }

  // Tertiary: check legacy field names
  const legacyImage = creator.profileImageUrl || creator.profileImage || creator.pfp;
  if (legacyImage) return legacyImage;

  return null;
};

/**
 * Get creator initials for avatar fallback
 * 
 * Extracts initials from various name fields with proper fallback
 * 
 * @param {Object} creator - The creator object
 * @returns {string} - Creator initials (max 2 characters)
 */
export const getCreatorInitials = (creator) => {
  if (!creator) return '??';

  const name = getCreatorHandle(creator) || getCleanCreatorName(creator);

  if (!name) return '??';

  // Clean the name and extract initials
  const cleanName = name.trim().replace(/^@/, '');
  
  if (cleanName.length === 0) return '??';

  // Split by spaces and take first letter of each word (max 2)
  const words = cleanName.split(/\s+/);
  
  if (words.length === 1) {
    // Single word - take first two characters
    return cleanName.substring(0, 2).toUpperCase();
  } else {
    // Multiple words - take first letter of first two words
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
  }
};

/**
 * Get creator display name with fallback
 * 
 * @param {Object} creator - The creator object
 * @returns {string} - Creator display name
 */
export const getCreatorDisplayName = (creator) => {
  if (!creator) return 'Unknown Creator';

  return getCreatorHandle(creator) || getCleanCreatorName(creator) || 'Unknown Creator';
};

/**
 * Get creator Twitter username with fallback
 * 
 * @param {Object} creator - The creator object
 * @returns {string} - Creator Twitter username (with @ prefix)
 */
export const getCreatorTwitterUsername = (creator) => {
  if (!creator) return '@unknown';

  return getCreatorHandle(creator) || '@unknown';
};

/**
 * Check if creator has a profile image
 * 
 * @param {Object} creator - The creator object
 * @returns {boolean} - True if creator has a profile image
 */
export const hasCreatorProfileImage = (creator) => {
  return getCreatorProfileImage(creator) !== null;
};
